import './index.scss';
import loggerService from '../../utils/loggerService';

export const SELECTORS = {
  BODY_LOCK: 'js-body-for-sl-armor-captcha',
  TAG: 'sl-armor-captcha',
  MASK: 'sl-armor-captcha-mask',
  WRAP: 'sl-armor-captcha-wrap',
};

export const E2E_IDS = {
  OVERLAY: 'armor-captcha-overlay_container',
  MASK: 'armor-captcha-mask_container',
  WRAP: 'armor-captcha-wrap_container',
};

export const ARMOR_CAPTCHA_SCENES = {
  REGISTRATION: 'ec1creg',
  REGISTRATION_SMS: 'ec1cregsm',
  PROFILE_UPDATE: 'ec1cmdfpesm',
  FORGOT_PASSWORD_SMS: 'ec1cfetpwdsm',
};

const APP_CODE = 'd2nrpf';

const setBodyLock = () => {
  if (document.querySelector(SELECTORS.TAG)) {
    document.body.classList.add(SELECTORS.BODY_LOCK);
  } else {
    document.body.classList.remove(SELECTORS.BODY_LOCK);
  }
};

const renderShell = ($el) => {
  $el.setAttribute('role', 'dialog');
  $el.setAttribute('aria-modal', 'true');
  $el.setAttribute('aria-label', 'Verification required');
  $el.setAttribute('tabindex', '-1');

  if ($el.querySelector(SELECTORS.MASK)) return;

  const $mask = document.createElement(SELECTORS.MASK);
  $mask.dataset.e2eId = E2E_IDS.MASK;
  $el.appendChild($mask);

  const $wrap = document.createElement(SELECTORS.WRAP);
  $wrap.id = SELECTORS.WRAP;
  $wrap.dataset.e2eId = E2E_IDS.WRAP;
  $el.appendChild($wrap);
};

const cleanupListeners = ($el) => {
  const $mask = $el.querySelector(SELECTORS.MASK);
  if (!$mask || !$el._onMaskClick) return;

  $mask.removeEventListener('click', $el._onMaskClick);
  $el._onMaskClick = null;
};

const resolveWith = ($el, value) => {
  const resolve = $el._resolve;
  resolve?.(value);
  SlArmorCaptcha.destroy();
};

const rejectWith = ($el, error) => {
  const reject = $el._reject;
  reject?.(error);
  SlArmorCaptcha.destroy();
};

const bindListeners = ($el) => {
  const $mask = $el.querySelector(SELECTORS.MASK);
  if (!$mask || $el._onMaskClick) return;

  $el._onMaskClick = () => {
    resolveWith($el, '');
  };

  $mask.addEventListener('click', $el._onMaskClick);
};

const solveElement = ($el, { captchaType, serialNo, scene, lang }) => {
  renderShell($el);
  bindListeners($el);

  try {
    new window.ArmorCaptcha({
      wrapId: SELECTORS.WRAP,
      appCode: APP_CODE,
      captchaScene: scene,
      captchaType,
      bizParam: { serialNo },
      lang: lang || getCurrentLang(),
      env: window.mainConfig?.shoplineCaptchaEnv,
      onSuccess: (captchaToken) => {
        resolveWith($el, captchaToken);
      },
    });
  } catch (e) {
    loggerService.error(e, {
      context: { scene, captchaType },
    });
    rejectWith($el, e);
  }
};

export class SlArmorCaptcha extends HTMLElement {
  constructor() {
    super();
    this._onMaskClick = null;
    this._resolve = null;
    this._reject = null;
  }

  connectedCallback() {
    // Pre-builds shell for body-lock safety; mountAndSolve renders it with full behaviour via solveElement.
    renderShell(this);
    setBodyLock();
  }

  disconnectedCallback() {
    cleanupListeners(this);
    setBodyLock();
  }

  static destroy() {
    document.querySelector(SELECTORS.TAG)?.remove();
    setBodyLock();
  }

  static mountAndSolve(options) {
    return new Promise((resolve, reject) => {
      SlArmorCaptcha.destroy();

      const $el = document.createElement(SELECTORS.TAG);
      $el.dataset.e2eId = E2E_IDS.OVERLAY;
      $el._resolve = resolve;
      $el._reject = reject;
      document.body.appendChild($el);
      solveElement($el, options);
    });
  }
}

if (!customElements.get(SELECTORS.TAG)) {
  customElements.define(SELECTORS.TAG, SlArmorCaptcha);
}

export const destroySlArmorCaptcha = () => SlArmorCaptcha.destroy();

const LOCALE_TO_ARMOR_LANG = {
  en: 'en',
  'zh-hant': 'zh-hant-tw',
  'zh-cn': 'zh-hans-cn',
  ms: 'ms',
  vi: 'vi',
  ja: 'ja',
  ko: 'ko',
  th: 'th',
  de: 'de',
  fr: 'fr',
  id: 'id',
};

const getCurrentLang = () => {
  const code = window.mainConfig?.localeData?.loadedLanguage?.code;
  return LOCALE_TO_ARMOR_LANG[code] ?? 'en';
};

export const mountAndSolve = (...args) => SlArmorCaptcha.mountAndSolve(...args);
